import React from 'react';
import { Link } from 'react-router-dom';

// Import logo assets
import logoImage from '../assets/logos/Spirit Falls Logo MainBW.png';

const VIEW_MODE_LABELS = {
  'single': 'Single Page',
  'double': 'Double Page'
};

const FIT_MODE_LABELS = {
  'fit-height': 'Fit Height',
  'fit-width': 'Fit Width',
  'fit-both': 'Fit Both'
};

function ViewerControls({
  isOpen,
  onToggle,
  chapter,
  navigableChapters,
  currentChapterId,
  frames,
  currentFrame,
  currentFrameIndex,
  viewMode,
  fitMode,
  onViewModeChange,
  onFitModeChange,
  onChapterChange,
  onPageChange,
  onPreviousChapter,
  onNextChapter,
  onPreviousFrame,
  onNextFrame,
  randomLogo
}) {
  const [isLogoHovered, setIsLogoHovered] = React.useState(false);

  const canGoPrevFrame = frames.length > 0 && currentFrameIndex > 0;
  const canGoNextFrame = frames.length > 0 && currentFrameIndex < frames.length - 1;

  const currentChapterIndex = navigableChapters.findIndex(ch => ch.id === currentChapterId);
  const canGoPrevChapter = currentChapterIndex > 0;
  const canGoNextChapter = currentChapterIndex >= 0 && currentChapterIndex < navigableChapters.length - 1;

  return (
    <>
      {/* Logo - only shown when sidebar is open */}
      {isOpen && (
        <Link
          to="/"
          className={`viewer-logo ${isLogoHovered ? 'hovered' : ''}`}
          onMouseEnter={() => setIsLogoHovered(true)}
          onMouseLeave={() => setIsLogoHovered(false)}
        >
          <img
            src={randomLogo}
            alt="Spirit Falls Logo"
            className="logo-color"
          />
          <img
            src={logoImage}
            alt="Spirit Falls Logo"
            className="logo-white"
          />
        </Link>
      )}

      {/* Controls Panel */}
      <aside className={`viewer-controls ${isOpen ? 'controls-open' : ''}`}>
        <button
          type="button"
          className="controls-toggle"
          onClick={onToggle}
          aria-label={isOpen ? "Close settings" : "Open settings"}
        >
          {isOpen ? '×' : '⚙'}
        </button>

        {isOpen && (
          <div className="controls-panel">
            {/* Chapter Navigation */}
            <div className="control-group">
              <label className="control-label">Chapter</label>
              <div className="control-row">
                <button
                  type="button"
                  className="nav-btn"
                  onClick={onPreviousChapter}
                  disabled={!canGoPrevChapter}
                  aria-label="Previous chapter"
                >
                  ←
                </button>
                <select
                  value={currentChapterId}
                  onChange={(e) => onChapterChange(e.target.value)}
                  className="control-select"
                >
                  {navigableChapters.map((ch) => (
                    <option key={ch.id} value={ch.id}>
                      Chapter {ch.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="nav-btn"
                  onClick={onNextChapter}
                  disabled={!canGoNextChapter}
                  aria-label="Next chapter"
                >
                  →
                </button>
              </div>
            </div>

            {/* Page Navigation */}
            <div className="control-group">
              <label className="control-label">Page</label>
              <div className="control-row">
                <button
                  type="button"
                  className="nav-btn"
                  onClick={onPreviousFrame}
                  disabled={!canGoPrevFrame}
                  aria-label="Previous page"
                >
                  ←
                </button>
                <select
                  value={currentFrame ? Math.min(...currentFrame.pages) : 0}
                  onChange={(e) => onPageChange(Number.parseInt(e.target.value, 10))}
                  className="control-select"
                >
                  {frames.map(frame => {
                    const firstPage = Math.min(...frame.pages);
                    const label = frame.pages.join('-');
                    return (
                      <option key={frame.key} value={firstPage}>
                        {label}
                      </option>
                    );
                  })}
                </select>
                <button
                  type="button"
                  className="nav-btn"
                  onClick={onNextFrame}
                  disabled={!canGoNextFrame}
                  aria-label="Next page"
                >
                  →
                </button>
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="control-group">
              <label className="control-label">View Mode</label>
              <button
                type="button"
                className="mode-btn"
                onClick={onViewModeChange}
              >
                {VIEW_MODE_LABELS[viewMode]}
              </button>
            </div>

            {/* Fit Mode Toggle */}
            <div className="control-group">
              <label className="control-label">Fit</label>
              <button
                type="button"
                className="mode-btn"
                onClick={onFitModeChange}
              >
                {FIT_MODE_LABELS[fitMode]}
              </button>
            </div>
          </div>
        )}
      </aside>
    </>
  );
}

export default ViewerControls;
